import * as XLSX from "xlsx";
import { algoliasearch } from "algoliasearch";
import { collection, documentId, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebaseConfig";
import {
  generateInvoiceV2,
  generateCombinedInvoiceV2,
} from "../utils/invoiceGeneratorV2";
import {
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_API_KEY,
  ALGOLIA_INDEX_NAME,
  buildNumericFilters,
  buildUiFiltersString,
} from "../utils/algoliaConfig";
import {
  getAllowedBillingEntitiesForProjects,
  isSupportedBillingEntity,
  resolveBillingEntitySelection,
} from "../utils/billingEntity";
import {
  filterProjects as filterProjectsLocally,
  hasLocalOnlyProjectFilters,
} from "../utils/projectFiltering";

const resolveInvoiceBillingEntity = (projects, requestedCompany) => {
  const allowedCompanies = getAllowedBillingEntitiesForProjects(projects);

  if (!allowedCompanies.length) {
    throw new Error(
      "Selected projects have conflicting billing entities. Update the project company before invoicing."
    );
  }

  if (
    isSupportedBillingEntity(requestedCompany) &&
    !allowedCompanies.includes(requestedCompany)
  ) {
    throw new Error(
      "Invoice billing entity must match the billing entity set on the project. Update the project first to change it."
    );
  }

  return resolveBillingEntitySelection(projects, requestedCompany || "UK");
};

export const createInvoiceForProject = async (invoiceOptions) => {
  const fallbackFormatMoney = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `£${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const fallbackFormatMoneyWithoutPoundSign = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  if (!invoiceOptions) {
    throw new Error("Invoice options are required to generate an invoice.");
  }

  const projectId = invoiceOptions.projectId || invoiceOptions.id;
  if (!projectId) {
    throw new Error("projectId is required to generate an invoice.");
  }

  const projects = await fetchProjectsByIds([projectId]);
  if (!projects.length) {
    throw new Error("Project not found.");
  }

  const selectedCompany = resolveInvoiceBillingEntity(
    projects,
    invoiceOptions.selectedCompany
  );

  const preparedOptions = {
    ...invoiceOptions,
    projectId,
    invoiceChoice: invoiceOptions.invoiceChoice || "Show subject",
    selectedCompany,
    useManualAED: invoiceOptions.useManualAED ?? false,
    showGBP: invoiceOptions.showGBP ?? true,
    exchangeRateSource:
      invoiceOptions.exchangeRateSource || "ExchangeRate-API",
    formatMoney: invoiceOptions.formatMoney || fallbackFormatMoney,
    formatMoneyWithoutPoundSign:
      invoiceOptions.formatMoneyWithoutPoundSign ||
      fallbackFormatMoneyWithoutPoundSign,
  };

  return generateInvoiceV2(preparedOptions);
};

export const createInvoiceForProjects = async (invoiceOptions) => {
  const fallbackFormatMoney = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `£${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const fallbackFormatMoneyWithoutPoundSign = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  if (!invoiceOptions) {
    throw new Error("Invoice options are required to generate an invoice.");
  }

  const projectIds = Array.isArray(invoiceOptions.projectIds)
    ? invoiceOptions.projectIds.filter(Boolean)
    : [];

  if (!projectIds.length) {
    const singleProjectId = invoiceOptions.projectId || invoiceOptions.id;
    if (!singleProjectId) {
      throw new Error("projectIds or projectId is required to generate an invoice.");
    }
    return createInvoiceForProject(invoiceOptions);
  }

  const projects = await fetchProjectsByIds(projectIds);
  if (!projects.length) {
    throw new Error("No matching projects found.");
  }

  const selectedCompany = resolveInvoiceBillingEntity(
    projects,
    invoiceOptions.selectedCompany
  );

  const preparedOptions = {
    ...invoiceOptions,
    projectIds,
    invoiceChoice: invoiceOptions.invoiceChoice || "Show subject",
    selectedCompany,
    invoiceNoOverride: invoiceOptions.invoiceNoOverride,
    useManualAED: invoiceOptions.useManualAED ?? false,
    showGBP: invoiceOptions.showGBP ?? true,
    exchangeRateSource:
      invoiceOptions.exchangeRateSource || "ExchangeRate-API",
    formatMoney: invoiceOptions.formatMoney || fallbackFormatMoney,
    formatMoneyWithoutPoundSign:
      invoiceOptions.formatMoneyWithoutPoundSign ||
      fallbackFormatMoneyWithoutPoundSign,
  };

  return generateCombinedInvoiceV2(preparedOptions);
};

const EXPORT_HITS_PER_PAGE = 1000;

const hasActiveFilters = (filters = {}) =>
  Object.values(filters).some((arr) => Array.isArray(arr) && arr.length > 0);

const ensureNumber = (value) => {
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const cleaned = value.replace(/,/g, "").trim();
    if (!cleaned) return null;
    const parsed = Number(cleaned);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const ensureNumberOrBlank = (value) => {
  const num = toNumberOrNull(value);
  return num === null ? "" : num;
};

const calculateVatAmount = (feeValue, totalIncVatValue, vatRateValue) => {
  const fee = toNumberOrNull(feeValue);
  const totalIncVat = toNumberOrNull(totalIncVatValue);

  if (fee !== null && totalIncVat !== null) {
    return totalIncVat - fee;
  }

  const vatRate = toNumberOrNull(vatRateValue);
  if (fee !== null && vatRate !== null) {
    return (fee * vatRate) / 100;
  }

  return "";
};

const calculateAmountIncVat = (feeValue, totalIncVatValue, vatRateValue) => {
  const totalIncVat = toNumberOrNull(totalIncVatValue);
  if (totalIncVat !== null) {
    return totalIncVat;
  }

  const fee = toNumberOrNull(feeValue);
  const vatRate = toNumberOrNull(vatRateValue);
  if (fee !== null && vatRate !== null) {
    return fee + (fee * vatRate) / 100;
  }

  return "";
};

const sumTaskValues = (tasks, key) => {
  let sum = 0;
  let hasValues = false;
  tasks.forEach((task) => {
    const num = toNumberOrNull(task?.[key]);
    if (num !== null) {
      sum += num;
      hasValues = true;
    }
  });
  return hasValues ? sum : "";
};

const getUniformTaskValue = (tasks, key) => {
  let uniformValue = null;
  let hasValue = false;
  for (const task of tasks) {
    const num = toNumberOrNull(task?.[key]);
    if (num === null) continue;
    if (!hasValue) {
      uniformValue = num;
      hasValue = true;
    } else if (uniformValue !== num) {
      return null;
    }
  }
  return hasValue ? uniformValue : null;
};

const resolveCompanyLabel = (company) => {
  if (company === "UK") return "Fort Corps Limited (UK)";
  if (company === "UAE") return "Fort Core LLC FZ (UAE)";
  return "Unassigned";
};

const createAlgoliaClient = () => {
  if (!ALGOLIA_APP_ID || !ALGOLIA_SEARCH_API_KEY) return null;
  try {
    return algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_API_KEY);
  } catch (error) {
    console.error("Failed to init Algolia client for export:", error);
    return null;
  }
};

const fetchAllProjects = async () => {
  const projectsCollection = collection(db, "projects");
  const projectsSnapshot = await getDocs(projectsCollection);
  return projectsSnapshot.docs.map((projectDoc) => ({
    id: projectDoc.id,
    ...projectDoc.data(),
  }));
};

const fetchProjectsByIds = async (ids = []) => {
  const uniqueIds = Array.from(new Set(ids.filter(Boolean)));
  if (!uniqueIds.length) return [];

  const projectsCollection = collection(db, "projects");
  const results = [];

  for (let i = 0; i < uniqueIds.length; i += 10) {
    const chunk = uniqueIds.slice(i, i + 10);
    const snapshot = await getDocs(
      query(projectsCollection, where(documentId(), "in", chunk))
    );
    snapshot.forEach((projectDoc) =>
      results.push({ id: projectDoc.id, ...projectDoc.data() })
    );
  }

  return results;
};

const fetchProjectsMatchingFilters = async (filters = {}, search = "") => {
  const client = createAlgoliaClient();

  if (client && !hasLocalOnlyProjectFilters(filters)) {
    try {
      const uiFiltersString = buildUiFiltersString(filters);
      const numericFilters = buildNumericFilters(filters);
      const numericFiltersString = numericFilters?.join(" AND ");
      const combinedFilters = [uiFiltersString, numericFiltersString]
        .filter(Boolean)
        .join(" AND ");

      const ids = new Set();
      let page = 0;
      let totalPages = 1;

      while (page < totalPages) {
        const res = await client.searchSingleIndex({
          indexName: ALGOLIA_INDEX_NAME,
          searchParams: {
            query: (search || "").trim(),
            page,
            hitsPerPage: EXPORT_HITS_PER_PAGE,
            ...(combinedFilters ? { filters: combinedFilters } : {}),
          },
        });

        (res?.hits || []).forEach((hit) => {
          const id = hit.id || hit.objectID;
          if (id) ids.add(id);
        });

        totalPages =
          typeof res?.nbPages === "number" && res.nbPages > 0
            ? res.nbPages
            : page + 1;
        page += 1;
        if (!res?.hits || res.hits.length === 0) break;
      }

      return fetchProjectsByIds([...ids]);
    } catch (error) {
      console.error("Failed to fetch filtered projects from Algolia:", error);
    }
  }

  // Fallback: fetch everything locally and apply filters/search in-memory
  const allProjects = await fetchAllProjects();
  return filterProjectsLocally(allProjects, filters, search);
};

export const exportProjectsToExcel = async (
  selectedProjects = [],
  options = {}
) => {
  const filters = options.filters || {};
  const search = options.search || "";
  const includeTasks = options.includeTasks ?? true;
  const hasFilters = hasActiveFilters(filters);
  const hasSearch = typeof search === "string" && search.trim().length > 0;

  let projectsData = [];

  if (selectedProjects.length > 0) {
    projectsData = await fetchProjectsByIds(selectedProjects);
  } else if (hasFilters || hasSearch) {
    projectsData = await fetchProjectsMatchingFilters(filters, search);
  } else {
    projectsData = await fetchAllProjects();
  }

  const excelData = projectsData.flatMap((project) => {
    const tasks = Array.isArray(project.tasks) ? project.tasks : [];
    const projectIncomeAedValue = toNumberOrNull(project.incomeAED);
    const projectAmountIncVatAedValue = toNumberOrNull(project.amountIncVatAED);
    const hasTaskAedValues = tasks.some(
      (task) =>
        toNumberOrNull(task?.incomeAED) !== null ||
        toNumberOrNull(task?.amountIncVatAED) !== null
    );
    const shouldIncludeAed =
      projectIncomeAedValue !== null ||
      projectAmountIncVatAedValue !== null ||
      hasTaskAedValues;
    const taskIncomeAedSum = sumTaskValues(tasks, "incomeAED");
    const taskAmountIncVatAedSum = sumTaskValues(tasks, "amountIncVatAED");
    const projectFeesAed = shouldIncludeAed
      ? projectIncomeAedValue !== null
        ? projectIncomeAedValue
        : taskIncomeAedSum
      : "";
    const projectAmountIncVatAed = shouldIncludeAed
      ? projectAmountIncVatAedValue !== null
        ? projectAmountIncVatAedValue
        : taskAmountIncVatAedSum
      : "";
    const projectVatAed = shouldIncludeAed
      ? project.vatAED !== undefined && project.vatAED !== null && `${project.vatAED}`.trim() !== ""
        ? ensureNumberOrBlank(project.vatAED)
        : ensureNumberOrBlank(getUniformTaskValue(tasks, "vatAED"))
      : "";
    const projectVatAmount = calculateVatAmount(
      project.income,
      project.amountIncVat,
      project.vat
    );
    const projectAmountIncVat = calculateAmountIncVat(
      project.income,
      project.amountIncVat,
      project.vat
    );
    const projectVatAmountAed = shouldIncludeAed
      ? calculateVatAmount(projectFeesAed, projectAmountIncVatAed, projectVatAed)
      : "";
    const resolvedProjectAmountIncVatAed = shouldIncludeAed
      ? calculateAmountIncVat(projectFeesAed, projectAmountIncVatAed, projectVatAed)
      : "";
    const companyLabel = resolveCompanyLabel(project.company);
    const baseProjectRow = {
      "Project Ref": project.projectRef,
      "Invoice Ref": project.invoiceNo || "",
      Company: companyLabel,
      "Project Manager": project.projectManager,
      Client: project.client,
      "Project Status": project.projectStatus,
      Fees: ensureNumber(project.income),
      "VAT %": ensureNumber(project.vat),
      "VAT Amount": ensureNumberOrBlank(projectVatAmount),
      "Amount Inc VAT": ensureNumberOrBlank(projectAmountIncVat),
      "Fees (AED)": projectFeesAed,
      "VAT % (AED)": projectVatAed,
      "VAT Amount (AED)": ensureNumberOrBlank(projectVatAmountAed),
      "Amount Inc VAT (AED)": ensureNumberOrBlank(
        resolvedProjectAmountIncVatAed
      ),
      "Invoice Raised": project.invoiceRaised,
      "Invoice Date": project.dateForInvoice,
      "Paid On": project.paidOn,
      "Payment Status": project.paymentStatus,
      "Payment Notes": project.paymentNotes,
      Cost: ensureNumber(project.cost),
      PoC: project.poc,
      "# Tasks": tasks.length,
    };

    if (!includeTasks) {
      return [baseProjectRow];
    }

    if (tasks.length === 0) {
      return [
        {
          ...baseProjectRow,
          "Task No": "",
          Subject: "",
          Country: "",
          Task: "",
          "Order Accepted": "",
          "Due Date": "",
          "Task Status": "",
          "Completed On": "",
          "Analyst 1": "",
          "Analyst 2": "",
          "Task Fees": 0,
          "Task VAT %": 0,
          "Task VAT Amount": 0,
          "Task Amount Inc VAT": 0,
          "Task Fees (AED)": "",
          "Task VAT % (AED)": "",
          "Task VAT Amount (AED)": "",
          "Task Amount Inc VAT (AED)": "",
        },
      ];
    }

    return tasks.map((task) => {
      const hasTaskAed =
        toNumberOrNull(task?.incomeAED) !== null ||
        toNumberOrNull(task?.amountIncVatAED) !== null;
      const taskVatAmount = calculateVatAmount(
        task.income,
        task.amountIncVat,
        task.vat
      );
      const taskAmountIncVat = calculateAmountIncVat(
        task.income,
        task.amountIncVat,
        task.vat
      );
      const taskVatRateAed = hasTaskAed
        ? ensureNumberOrBlank(task.vatAED)
        : "";
      const taskVatAmountAed = hasTaskAed
        ? calculateVatAmount(task.incomeAED, task.amountIncVatAED, task.vatAED)
        : "";
      const taskAmountIncVatAed = hasTaskAed
        ? calculateAmountIncVat(
            task.incomeAED,
            task.amountIncVatAED,
            task.vatAED
          )
        : "";

      return {
        ...baseProjectRow,
        "Task No": task.taskNo,
        Subject: task.subject,
        Country: task.country,
        Task: task.task,
        "Order Accepted": task.orderAccepted,
        "Due Date": task.dueDate,
        "Task Status": task.taskStatus,
        "Completed On": task.completedOn,
        "Analyst 1": task.analyst1,
        "Analyst 2": task.analyst2,
        "Task Fees": ensureNumber(task.income),
        "Task VAT %": ensureNumber(task.vat),
        "Task VAT Amount": ensureNumberOrBlank(taskVatAmount),
        "Task Amount Inc VAT": ensureNumberOrBlank(taskAmountIncVat),
        "Task Fees (AED)": hasTaskAed ? ensureNumberOrBlank(task.incomeAED) : "",
        "Task VAT % (AED)": taskVatRateAed,
        "Task VAT Amount (AED)": hasTaskAed
          ? ensureNumberOrBlank(taskVatAmountAed)
          : "",
        "Task Amount Inc VAT (AED)": hasTaskAed
          ? ensureNumberOrBlank(taskAmountIncVatAed)
          : "",
      };
    });
  });

  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    includeTasks ? "Projects and Tasks" : "Projects"
  );
  XLSX.writeFile(
    workbook,
    includeTasks ? "projects_and_tasks_export.xlsx" : "projects_export.xlsx"
  );
};

export default {
  createInvoiceForProject,
  exportProjectsToExcel,
};
